import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Search } from "lucide-react";
import { lineDisplayValue, money, newLine } from "./orderLineUtils";

export function OrderItemsTable({ lines, calc, products, intraState, setLines, setLine, pickProduct }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="section-items">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Section 3 — Products (unlimited SKUs)</p>
        <Button size="sm" variant="outline" onClick={() => setLines([...lines, newLine()])} data-testid="add-product-button"><Plus className="w-4 h-4 mr-1" /> Add Product</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="border-b border-border text-left">
              {["#", "SKU / Product", "HSN", "Qty", "UOM", "Rate ₹", "Disc %", "Taxable ₹", "GST %", `Tax ₹ (${intraState ? "C+S" : "IGST"})`, "Total ₹", "Stock", ""].map((h) => (
                <th key={h} className="px-2 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calc.rows.map((l, i) => (
              <tr key={l._uid} className="border-b border-border/50" data-testid={`line-row-${i}`}>
                <td className="px-2 py-2 metric text-muted-foreground">{i + 1}</td>
                <td className="px-2 py-2 relative min-w-56">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input className="pl-7 h-9 font-mono text-xs" data-testid={`line-sku-search-${i}`} placeholder="Search SKU / name / HSN"
                      value={lineDisplayValue(l)}
                      onFocus={() => setLine(i, { _open: true })}
                      onBlur={() => setTimeout(() => setLine(i, { _open: false }), 200)}
                      onChange={(e) => setLine(i, { _q: e.target.value, _open: true })} />
                    {l._open && (
                      <div className="absolute z-30 mt-1 w-80 max-h-56 overflow-y-auto bg-popover border border-border rounded-md shadow-lg" data-testid={`line-sku-dropdown-${i}`}>
                        {products.filter((p) => !l._q || `${p.sku} ${p.name} ${p.hsn} ${p.category}`.toLowerCase().includes(l._q.toLowerCase())).slice(0, 12).map((p) => (
                          <button key={p.sku} type="button" className="w-full text-left px-3 py-2 text-xs hover:bg-accent transition-colors" onMouseDown={() => pickProduct(i, p)} data-testid={`line-sku-option-${i}-${p.sku}`}>
                            <span className="metric font-bold text-primary">{p.sku}</span> {p.name}
                            <span className="block text-muted-foreground">HSN {p.hsn} · ₹{p.standard_rate} · GST {p.gst_percent}% · free stock {p.stock?.free?.toLocaleString()}</span>
                          </button>
                        ))}
                        {products.length === 0 && <p className="px-3 py-2 text-xs text-muted-foreground">No products</p>}
                      </div>
                    )}
                  </div>
                  {l.sku && <Input className="mt-1 h-7 text-[10px] font-mono" placeholder="Customer SKU (optional)" data-testid={`line-customer-sku-${i}`} value={l.customer_sku} onChange={(e) => setLine(i, { customer_sku: e.target.value })} />}
                </td>
                <td className="px-2 py-2 metric text-xs">{l.hsn || "—"}</td>
                <td className="px-2 py-2 w-28"><Input type="number" className="h-9" data-testid={`line-qty-${i}`} value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                <td className="px-2 py-2 text-xs">{l.uom}</td>
                <td className="px-2 py-2 w-28">
                  <Input type="number" step="0.01" className="h-9" data-testid={`line-rate-${i}`} value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value, _cust_price: false })} />
                  {l._cust_price && <p className="text-[9px] font-mono text-primary mt-0.5">customer price applied</p>}
                  {(() => {
                    const r = parseFloat(l.rate) || 0;
                    const eff = r * (1 - (parseFloat(l.discount_pct) || 0) / 100);
                    if (l.cost_price && eff < l.cost_price) return <p className="text-[9px] font-mono text-signal-red mt-0.5" data-testid={`line-price-flag-${i}`}>below cost ₹{l.cost_price} — MD approval required</p>;
                    if (l.floor_price && eff < l.floor_price) return <p className="text-[9px] font-mono text-signal-red mt-0.5" data-testid={`line-price-flag-${i}`}>below floor ₹{l.floor_price} — MD approval required</p>;
                    if (l.msp && eff < l.msp) return <p className="text-[9px] font-mono text-signal-amber mt-0.5" data-testid={`line-price-flag-${i}`}>below MSP ₹{l.msp} — approval required</p>;
                    return null;
                  })()}
                </td>
                <td className="px-2 py-2 w-20"><Input type="number" step="0.01" className="h-9" data-testid={`line-disc-${i}`} value={l.discount_pct} onChange={(e) => setLine(i, { discount_pct: e.target.value })} /></td>
                <td className="px-2 py-2 metric text-xs">{money(l.taxable)}</td>
                <td className="px-2 py-2 w-20"><Input type="number" className="h-9" data-testid={`line-gst-${i}`} value={l.gst_percent} onChange={(e) => setLine(i, { gst_percent: e.target.value })} /></td>
                <td className="px-2 py-2 metric text-xs">{money(l.tax)}</td>
                <td className="px-2 py-2 metric text-xs font-bold">{money(l.total)}</td>
                <td className="px-2 py-2 text-xs whitespace-nowrap" data-testid={`line-stock-${i}`}>
                  {l.stock ? (
                    <span className={l.stock.free >= (parseFloat(l.quantity) || 0) ? "text-signal-green" : "text-signal-red"}>
                      {l.stock.free >= (parseFloat(l.quantity) || 0) ? "🟢" : "🔴"} free {l.stock.free?.toLocaleString()}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-2 py-2">
                  {lines.length > 1 && <Button size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`line-remove-${i}`}><Trash2 className="w-4 h-4 text-signal-red" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
